import { useState, useEffect } from 'react';
import { apiUrl } from '../services/api';

/**
 * Debounced place suggestion search.
 * Replaces 4 near-identical useEffect blocks across TripSetup.js and Itinerary.js.
 *
 * @param {string}  query   - the search string (reacts to changes)
 * @param {boolean} enabled - set to false to suppress fetching (e.g. when edit mode is closed)
 * @param {number}  limit   - max results (default 6)
 *
 * Returns:
 *   suggestions   - array of place objects
 *   activeIndex   - keyboard-highlighted index (-1 = none)
 *   setActiveIndex
 *   clearSuggestions - resets both suggestions and activeIndex
 *   selectSuggestion(s) - helper: returns display label from a suggestion object
 */
export function usePlaceSuggestions(query, { enabled = true, limit = 6 } = {}) {
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const q = String(query || '').trim();
    if (!enabled || q.length < 2) {
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(apiUrl(`/api/places/suggest?q=${encodeURIComponent(q)}&limit=${limit}`), {
        signal: controller.signal,
      })
        .then(res => (res.ok ? res.json() : []))
        .then(data => {
          if (Array.isArray(data)) {
            setSuggestions(data);
            setActiveIndex(-1);
          }
        })
        .catch(() => {});
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, enabled, limit]);

  const clearSuggestions = () => {
    setSuggestions([]);
    setActiveIndex(-1);
  };

  return { suggestions, activeIndex, setActiveIndex, clearSuggestions };
}
